"use client";

import { useEffect } from "react";
import {
  Title,
  Box,
  Anchor as MantineAnchor,
  Box as MantineBox,
  Image as MantineImage,
  Text as MantineText,
} from "@mantine/core";
import Link from "@/components/Link";
import Breadcrumb from "@/components/Breadcrumb";
import FollowButton from "@/components/FollowButton";
import BrandStatsBar from "@/components/BrandStatsBar";
import PopularModels from "@/components/PopularModels";
import CardGrid from "@/components/CardGrid";
import { NoResultsRescue } from "@/components/SearchRescue";
import { useSearchContext } from "@/context/searchContext";
import { useGlobalData } from "@/context/globalContext";
import { generateBrandSlug, formatCount } from "@/lib/shared/utils";
import { useBrandPriceStats } from "@/hooks/useBrandPriceStats";
import { useItemsStore } from "@/stores/itemsStore";
import { useUiStore } from "@/stores/uiStore";
import MobileMenu from "@/components/layout/MobileMenu";
import SignInPrompt from "@/components/SignInPrompt";
import Globe from "@/icons/Globe";
import InstagramOutline from "@/icons/InstagramOutline";
import { sendGtagEvent } from "@/lib/client/gtag";
import { BrandDescription } from "./BrandDescription";

import styles from "./BrandSearchPage.module.scss";

interface BrandSearchPageProps {
  brand: BrandFilter;
}

export default function BrandSearchPage({ brand }: BrandSearchPageProps) {
  const { setPreloadBrand, loadOnScroll, items, loading, noResults } = useSearchContext();

  const {
    stats: priceStats,
    topMarketplaces,
    topIndependentVendors,
  } = useBrandPriceStats(brand.id);
  const { brands: loadedBrands } = useGlobalData();
  const itemModalOpen = useUiStore((state) => state.itemModalOpen);
  const favoritesModalOpen = useUiStore((state) => state.favoritesModalOpen);
  const priceMonitorOpen = useUiStore((state) => state.priceMonitorOpen);
  const showSignInPrompt = useItemsStore((state) => state.showSignInPrompt);
  const setShowSignInPrompt = useItemsStore((state) => state.actions.setShowSignInPrompt);
  const brandInitial = brand.label.charAt(0).toUpperCase();

  useEffect(() => {
    const foundBrand = loadedBrands?.find(
      (b) => generateBrandSlug(b.label) === generateBrandSlug(brand.label)
    );
    if (foundBrand) {
      setPreloadBrand(foundBrand.label);
    }
  }, [loadedBrands, brand.label, setPreloadBrand]);

  // Update document title with listing count when stats are available
  useEffect(() => {
    if (priceStats && priceStats.totalCount > 0) {
      const listingCount = formatCount(priceStats.totalCount);
      document.title = `Discover ${listingCount} Secondhand ${brand.label} items | KURB`;
    }
  }, [priceStats, brand.label]);

  const hasStats = !noResults && priceStats !== null;
  const stats = hasStats
    ? {
        totalListings: priceStats!.totalCount,
        averagePrice: priceStats!.avgPrice,
        medianPrice: priceStats!.medianPrice,
        q1Price: priceStats!.q1Price,
        q3Price: priceStats!.q3Price,
        minPrice: priceStats!.minPrice,
        maxPrice: priceStats!.maxPrice,
      }
    : null;

  return (
    <>
      <MobileMenu hideFilterBar />
      <MantineBox
        className={styles.brandPage}
        data-slidein-open={itemModalOpen || favoritesModalOpen || priceMonitorOpen}
      >
        <aside className={styles.sidebar}>
          <MantineBox className={styles.sidebarContent}>
            <MantineBox className={styles.brandIdentity}>
              <Breadcrumb items={[{ text: "Brands", link: "/brands" }, { text: brand.label }]} />
              <MantineBox className={styles.brandLogo}>
                {brand.image ? (
                  <MantineImage src={brand.image} alt={brand.label} width={80} height={80} />
                ) : (
                  <MantineText span className={styles.brandInitial}>
                    {brandInitial}
                  </MantineText>
                )}
              </MantineBox>
              <Title order={1} className={styles.brandName}>
                {brand.label}
              </Title>
              <MantineBox className={styles.followButtonHidden}>
                <FollowButton type="brand" id={brand.id} size="large" brand={brand} />
              </MantineBox>
              {(brand.website || brand.instagram) && (
                <MantineBox className={styles.socialLinks}>
                  {brand.website && (
                    <MantineAnchor
                      href={brand.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.socialLink}
                      aria-label={`Visit ${brand.label} website`}
                      onClick={() =>
                        sendGtagEvent("brand_website_click", "outbound", brand.label ?? "", {
                          brand: brand.label,
                        })
                      }
                    >
                      <Globe size={24} />
                    </MantineAnchor>
                  )}
                  {brand.instagram && (
                    <MantineAnchor
                      href={`https://www.instagram.com/${brand.instagram.replace(/^@/, "")}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.socialLink}
                      aria-label={`Visit ${brand.label} on Instagram`}
                      onClick={() =>
                        sendGtagEvent("brand_instagram_click", "outbound", brand.label ?? "", {
                          brand: brand.label,
                        })
                      }
                    >
                      <InstagramOutline size={24} />
                    </MantineAnchor>
                  )}
                </MantineBox>
              )}
              {brand.description && (
                <BrandDescription
                  description={brand.description}
                  className={styles.descriptionSection}
                />
              )}
            </MantineBox>
            {stats && (
              <Box visibleFrom="lg">
                <BrandStatsBar
                  stats={stats}
                  topMarketplaces={topMarketplaces}
                  topIndependentVendors={topIndependentVendors}
                />
              </Box>
            )}
            <MantineBox className={styles.popularModelsSection}>
              <PopularModels brand={brand} />
              <Link href="/search" className={styles.advancedSearchLink}>
                ADVANCED SEARCH
              </Link>
            </MantineBox>
          </MantineBox>
        </aside>

        <main className={styles.mainContent} onScroll={loadOnScroll}>
          <MantineBox className={styles.mobileBrandIdentity}>
            <Breadcrumb items={[{ text: "Brands", link: "/brands" }, { text: brand.label }]} />
            <MantineBox className={styles.mobileBrandHeader}>
              <MantineBox className={styles.brandLogo}>
                {brand.image ? (
                  <MantineImage src={brand.image} alt={brand.label} width={80} height={80} />
                ) : (
                  <MantineText span className={styles.brandInitial}>
                    {brandInitial}
                  </MantineText>
                )}
              </MantineBox>
              <MantineBox className={styles.mobileBrandInfo}>
                <Title order={1} className={styles.brandName}>
                  {brand.label}
                </Title>
                <MantineBox className={styles.followButtonHidden}>
                  <FollowButton type="brand" id={brand.id} size="large" brand={brand} />
                </MantineBox>
                {(brand.website || brand.instagram) && (
                  <MantineBox className={styles.socialLinks}>
                    {brand.website && (
                      <MantineAnchor
                        href={brand.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.socialLink}
                        aria-label={`Visit ${brand.label} website`}
                      >
                        <Globe size={24} />
                      </MantineAnchor>
                    )}
                    {brand.instagram && (
                      <MantineAnchor
                        href={`https://www.instagram.com/${brand.instagram.replace(/^@/, "")}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.socialLink}
                        aria-label={`Visit ${brand.label} on Instagram`}
                      >
                        <InstagramOutline size={24} />
                      </MantineAnchor>
                    )}
                  </MantineBox>
                )}
              </MantineBox>
            </MantineBox>

            {brand.description && (
              <BrandDescription
                description={brand.description}
                className={styles.descriptionSection}
              />
            )}

            {stats && (
              <BrandStatsBar
                stats={stats}
                topMarketplaces={topMarketplaces}
                topIndependentVendors={topIndependentVendors}
              />
            )}
          </MantineBox>

          <MantineBox className={styles.mobilePopularModels}>
            <PopularModels brand={brand} />
            <Link href="/search" className={styles.advancedSearchLink}>
              ADVANCED SEARCH
            </Link>
          </MantineBox>

          <CardGrid
            items={items}
            loading={loading}
            noResults={noResults}
            showProAd={false}
            enableImageSearch
          />
          {noResults && !loading && <NoResultsRescue />}
        </main>
      </MantineBox>
      {showSignInPrompt && <SignInPrompt onClose={() => setShowSignInPrompt(false)} />}
    </>
  );
}
